/**
 * Public Trophy Room ViewModel
 *
 * Handles all business logic for the Public Trophy Room page.
 * Strict MVVM pattern - all logic here, page component is pure view.
 *
 * Structure: { state, actions, computed, onMount }
 */

import { useMemo, useState, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  fetchPublicTrophyRoomAsync,
  selectPublicTrophyRoom,
  clearTrophyRoom,
} from '@/store/slices/publicTrophyRoomSlice';
import type { LinkedNfcItem } from '@/domain/ports/IPublicTrophyRoomGateway';

export type FilterMode = 'all' | 'patches' | 'clothing';

export interface UsePublicTrophyRoomViewModelReturn {
  state: {
    userId: string;
    profile: ReturnType<typeof selectPublicTrophyRoom>['user'];
    socialLinks: ReturnType<typeof selectPublicTrophyRoom>['socialLinks'];
    wallets: ReturnType<typeof selectPublicTrophyRoom>['wallets'];
    items: ReturnType<typeof selectPublicTrophyRoom>['items'];
    loading: boolean;
    error: string | null;
    filterMode: FilterMode;
  };
  actions: {
    fetchTrophyRoom: () => Promise<void>;
    setFilterMode: (mode: FilterMode) => void;
    refresh: () => Promise<void>;
  };
  computed: {
    filteredItems: LinkedNfcItem[];
    hasItems: boolean;
    publicItemCount: number;
    hasSocialLinks: boolean;
    hasWallets: boolean;
    isEmptyState: boolean;
    filteredItemCount: number;
  };
  onMount: () => void;
}

export const usePublicTrophyRoomViewModel = (
  userId: string
): UsePublicTrophyRoomViewModelReturn => {
  const dispatch = useAppDispatch();
  const trophyRoom = useAppSelector(selectPublicTrophyRoom);
  const [filterMode, setFilterMode] = useState<FilterMode>('all');

  // Actions
  const actions = {
    fetchTrophyRoom: useCallback(async () => {
      await dispatch(fetchPublicTrophyRoomAsync(userId));
    }, [dispatch, userId]),

    setFilterMode: useCallback((mode: FilterMode) => {
      setFilterMode(mode);
    }, []),

    refresh: useCallback(async () => {
      // Clear current data and refetch
      dispatch(clearTrophyRoom());
      await dispatch(fetchPublicTrophyRoomAsync(userId));
    }, [dispatch, userId]),
  };

  // State
  const state = {
    userId,
    profile: trophyRoom.user,
    socialLinks: trophyRoom.socialLinks,
    wallets: trophyRoom.wallets,
    items: trophyRoom.items,
    loading: trophyRoom.loading,
    error: trophyRoom.error,
    filterMode,
  };

  // Computed values
  const computed = {
    filteredItems: useMemo(() => {
      if (filterMode === 'all') {
        return state.items;
      }

      if (filterMode === 'patches') {
        // Only items with productType === 'patch' or 'patches'
        return state.items.filter(
          (item) =>
            item.product?.productType === 'patch' ||
            item.product?.productType === 'patches'
        );
      }

      if (filterMode === 'clothing') {
        // All non-patch types are clothing (hoodie, jacket, shirt, pants, etc.)
        return state.items.filter(
          (item) =>
            item.product?.productType !== 'patch' &&
            item.product?.productType !== 'patches'
        );
      }

      return state.items;
    }, [state.items, filterMode]),

    hasItems: state.items.length > 0,

    publicItemCount: state.profile?.publicItemCount ?? 0,

    hasSocialLinks: state.socialLinks.length > 0,

    hasWallets: state.wallets.filter(w => {
      const hasAddress = w.address != null && w.address.trim() !== "";
      const hasEnsName = w.ensName != null && w.ensName.trim() !== "";
      return hasAddress || hasEnsName;
    }).length > 0,

    // Empty state is when data has been fetched (profile exists), no error, not loading, and no public items
    isEmptyState: !state.loading && !state.error && state.profile !== null && state.items.length === 0,

    filteredItemCount: 0, // Will be computed from filteredItems length
  };

  // Recompute filtered item count (can't be in useMemo above due to circular dependency)
  computed.filteredItemCount = computed.filteredItems.length;

  // onMount - called once when component mounts
  const onMount = useCallback(() => {
    dispatch(fetchPublicTrophyRoomAsync(userId));

    // Cleanup: clear Trophy Room data when unmounting
    return () => {
      dispatch(clearTrophyRoom());
    };
  }, [dispatch, userId]);

  return { state, actions, computed, onMount };
};
